import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Breadcrumbs as MUIBreadcrumbs, Typography, Box } from "@mui/material";
import { Icon } from "@iconify/react";
import { useContent } from "../../hooks/useContent";

/**
 * Breadcrumbs Component
 * 
 * Displays a beautiful, animated breadcrumb navigation trail
 * that shows the user's current location in the application.
 * 
 * Features:
 * - Automatically generates breadcrumbs based on the current route
 * - Hides on the home page
 * - Uses dynamic page headings from useContent hook
 * - Handles routes with dynamic parameters (IDs)
 * - Includes smooth animations and hover effects
 * - Fully responsive design
 * 
 * To add a new route label, update the routeLabels object below.
 */

const routeLabels = {
  "/home": "Home",
  "/email-support": "Email Support",
  "/meetings": "Web Conference",
  "/support-tickets": "Support Tickets",
  "/support-tickets/create-ticket": "Create Ticket",
  "/support-tickets/edit-ticket": "Edit Ticket",
  "/create-company": "Create Company Account",
  "/manage-companies": "Existing Company Licenses",
  "/manage-companies/update-company": "Update Company",
};

const Breadcrumbs = () => {
  const location = useLocation();
  const { pageHeading } = useContent();
  
  // Don't show breadcrumbs on home page
  if (location.pathname === "/home" || location.pathname === "/") {
    return null;
  }

  const pathnames = location.pathname.split("/").filter((x) => x);
  
  // Build breadcrumb items
  const breadcrumbItems = [
    { path: "/home", label: "Home" }
  ];

  let currentPath = "";
  pathnames.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Skip dynamic segments (like IDs) unless they're the last segment
    if (!isNaN(segment) || /^[0-9a-f-]{36}$/i.test(segment)) {
      return;
    }
    
    const label = routeLabels[currentPath] || segment
      .split("-")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    
    breadcrumbItems.push({ path: currentPath, label });
  });

  return (
    <Box
      className="breadcrumb-container"
      sx={{
        marginTop: "12px",
        transition: "all 0.3s ease",
      }}
    >
      <MUIBreadcrumbs
        separator={
          <Icon
            icon="mdi:chevron-right"
            style={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "20px" }}
          />
        }
        aria-label="breadcrumb"
        sx={{
          "& .MuiBreadcrumbs-ol": {
            flexWrap: "nowrap",
            justifyContent: "center",
          },
        }}
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          
          if (isLast) {
            return (
              <Box
                key={item.path}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Icon
                  icon="mdi:circle"
                  style={{
                    fontSize: "8px",
                    color: "#ffffff",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#ffffff",
                    fontSize: "14px",
                    letterSpacing: "0.2px",
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  {pageHeading || item.label}
                </Typography>
              </Box>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateX(2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateX(0)";
              }}
            >
              {index === 0 && (
                <Icon
                  icon="mdi:home"
                  style={{
                    fontSize: "18px",
                    color: "rgba(255, 255, 255, 0.8)",
                  }}
                />
              )}
              <Typography
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": {
                    color: "#ffffff",
                  },
                  transition: "color 0.2s ease",
                }}
              >
                {item.label}
              </Typography>
            </Link>
          );
        })}
      </MUIBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;

